import React from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardMedia, 
  CardContent, 
  Button, 
  Divider,
  Paper,
  useTheme
} from '@mui/material';
import { 
  FaLightbulb, 
  FaHandshake, 
  FaGlobe, 
  FaStar, 
  FaUserAlt, 
  FaChild, 
  FaMobile, 
  FaUsers, 
  FaLinkedin 
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTES } from '../../utils/constants';

/**
 * About page component
 */
const About = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Team members data
  const teamMembers = [
    {
      id: 1,
      name: 'Wairimu Kimemia',
      role: 'Founder & CEO',
      linkedIn: 'https://www.linkedin.com/in/margaret-wairimu-meira',
      image: 'https://res.cloudinary.com/digitaliquids-cloud-technologies/image/upload/WhatsApp_Image_2025-04-26_at_23.49.27_eocg0r.jpg'
    },
    {
      id: 1,
      name: 'Lorna Wanjiru',
      role: 'Advisory Board',
      linkedIn: 'https://www.linkedin.com/in/lorna-wanjiru-0488b739',
      image: 'https://res.cloudinary.com/digitaliquids-cloud-technologies/image/upload/WhatsApp_Image_2025-04-26_at_23.18.12_na4c4j.jpg'
    },
    {
      id: 2,
      name: 'Edgar Temba',
      role: 'CTO',
      linkedIn: 'https://linkedin.com/in/edgar-temba',
      image: 'https://res.cloudinary.com/digitaliquids-cloud-technologies/image/upload/40558646_2_u43bmo.jpg'
    },
    {
      id: 3,
      name: 'Millicent Muigai',
      role: 'Community Manager',
      linkedIn: 'https://www.linkedin.com/in/millicent-muigai-m',
      image: 'https://res.cloudinary.com/digitaliquids-cloud-technologies/image/upload/WhatsApp_Image_2025-04-27_at_00.01.19_v2jg40.jpg'
    },
    {
      id: 4,
      name: 'Kabugi Mbae',
      role: 'Content Manager',
      linkedIn: 'https://www.linkedin.com/in/kabugi-m-0938a351',
      image: 'https://res.cloudinary.com/digitaliquids-cloud-technologies/image/upload/WhatsApp_Image_2025-04-26_at_23.57.46_bkrgrp.jpg'
    }
  ];

  // Values data
  const values = [
    {
      id: 1,
      name: 'Purpose-Driven Impact',
      description: 'At Meira, we aim to empower families by connecting them with the resources they need to make informed educational choices. Every feature on our platform is crafted to drive meaningful change and support Kenya\'s education landscape, helping parents and students thrive.',
      icon: <FaLightbulb style={{ color: theme.palette.primary.main, fontSize: 24 }} />
    },
    {
      id: 2,
      name: 'Community and Connection',
      description: 'We believe in the strength of the community. Meira brings together parents, students, child-development experts, educators and schools in a trusted, supportive environment where everyone can grow and learn. We aim to create a foundation of collaboration and shared success by fostering connection.',
      icon: <FaHandshake style={{ color: theme.palette.primary.main, fontSize: 24 }} />
    },
    {
      id: 3,
      name: 'Innovation for Growth',
      description: 'Meira is dedicated to staying at the forefront of educational resources and technology. We are constantly evolving to provide more accessible and innovative solutions that simplify the journey of parenting.',
      icon: <FaGlobe style={{ color: theme.palette.primary.main, fontSize: 24 }} />
    }
  ];

  // Timeline data
  const timeline = [
    {
      id: 1,
      year: '2018',
      title: 'Meira Founded',
      description: 'Meira was established with a vision to transform education through technology.'
    },
    {
      id: 2,
      year: '2019',
      title: 'First Platform Launch',
      description: 'Released our initial platform connecting teachers and students in a virtual environment.'
    },
    {
      id: 3,
      year: '2020',
      title: 'Rapid Growth',
      description: 'In response to global educational challenges, we expanded our services and user base significantly.'
    },
    {
      id: 4,
      year: '2021',
      title: 'International Expansion',
      description: 'Began serving schools and families across multiple countries, adapting to diverse educational needs.'
    },
    {
      id: 5,
      year: '2022',
      title: 'Comprehensive Solution',
      description: 'Launched our integrated platform for schools, teachers, parents, and mentors.'
    },
    {
      id: 6,
      year: '2023',
      title: 'The Future',
      description: 'Continuing to innovate and expand our reach to improve education worldwide.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>About Meira | Our Story and Mission</title>
        <meta name="description" content="Learn about Meira's mission to transform education, our team, values, and journey since our founding." />
      </Helmet>

      {/* Hero section */}
      <Box 
        sx={{
          bgcolor: theme.palette.primary.main,
          color: 'white',
          py: 8,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="overline" fontWeight="bold">
                About Meira
              </Typography>
              <Typography variant="h2" component="h1" fontWeight="bold" gutterBottom>
                Transforming Education Through Technology
              </Typography>
              <Typography variant="h6" sx={{ mb: 4, maxWidth: '90%' }}>
                Our commitment to making education accessible, equitable, and effective
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box 
                component="img"
                src="https://res.cloudinary.com/digitaliquids-cloud-technologies/image/upload/meira_logo_design_transparent_cj7blv.png"
                alt="Meira Education"
                sx={{ 
                  width: '100%', 
                  maxWidth: 400,
                  mx: 'auto',
                  display: 'block',
                  borderRadius: 2,
                  boxShadow: 3
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Mission section */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="overline" 
              component="div" 
              color="primary" 
              fontWeight="bold"
            >
              Our Purpose
            </Typography>
            <Typography 
              variant="h3" 
              component="h2" 
              fontWeight="bold" 
              gutterBottom
            >
              Why We Exist
            </Typography>
            
            <Box sx={{ mt: 4, maxWidth: 800, mx: 'auto' }}>
              <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom>
                Our Mission:
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
                To empower parents through inclusive, accessible, and tech-enabled solutions that support their child's academic, emotional, and social development.
              </Typography>
              
              <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom>
                Our Vision:
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                To bridge educational gaps by connecting every child to the tools, resources, and opportunities they need to learn, grow, and succeed—regardless of their background.
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 6 }} />

          <Grid container spacing={4}>
            {values.map((value) => (
              <Grid item xs={12} md={4} key={value.id}>
                <Paper
                  elevation={2}
                  sx={{ 
                    p: 4, 
                    height: '100%', 
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ mr: 2 }}>{value.icon}</Box>
                    <Typography variant="h6" fontWeight="bold">
                      {value.name}
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {value.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* What Makes Us Different section */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="overline" 
              component="div" 
              color="primary" 
              fontWeight="bold"
            >
              What Makes Us Different
            </Typography>
            <Typography 
              variant="h3" 
              component="h2" 
              fontWeight="bold" 
              gutterBottom
            >
              Our Promise to You
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{ 
                  p: 3, 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  borderTop: `4px solid ${theme.palette.primary.main}`,
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 4
                  }
                }}
              >
                <Box 
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'white',
                    mb: 2
                  }}
                >
                  <FaUserAlt size={24} />
                </Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Localization First
                </Typography>
                <Typography variant="body2">
                  Built with the realities of African users in mind
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{ 
                  p: 3, 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  borderTop: `4px solid ${theme.palette.primary.main}`,
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 4
                  }
                }}
              >
                <Box 
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'white',
                    mb: 2
                  }}
                >
                  <FaChild size={24} />
                </Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  AI with Purpose
                </Typography>
                <Typography variant="body2">
                  Technology designed for equity, not just efficiency
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{ 
                  p: 3, 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  borderTop: `4px solid ${theme.palette.primary.main}`,
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 4
                  }
                }}
              >
                <Box 
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'white',
                    mb: 2
                  }}
                >
                  <FaMobile size={24} />
                </Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Multi-Stakeholder
                </Typography>
                <Typography variant="body2">
                  Tools for families, schools, and professionals
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{ 
                  p: 3, 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  borderTop: `4px solid ${theme.palette.primary.main}`,
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 4
                  }
                }}
              >
                <Box 
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'white',
                    mb: 2
                  }}
                >
                  <FaUsers size={24} />
                </Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Trust and Values-Driven
                </Typography>
                <Typography variant="body2">
                  Grounded in faith, impact, and community
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>


      {/* Team section */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="overline" 
              component="div" 
              color="primary" 
              fontWeight="bold"
            >
              Our Team
            </Typography>
            <Typography 
              variant="h3" 
              component="h2" 
              fontWeight="bold" 
              gutterBottom
            >
              Meet the People Behind Meira
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                fontSize: '1.1rem',
                mb: 4
              }}
            >
              Our diverse team of educators, technologists, and business leaders work together to create the best educational platform possible.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {teamMembers.map((member) => (
              <Grid item xs={12} sm={6} md={3} key={member.id}>
                <Card 
                  elevation={3}
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: 6
                    }
                  }}
                >
                  <CardMedia
                    component="img"
                    height="240"
                    image={member.image}
                    alt={member.name}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography gutterBottom variant="h6" component="div" fontWeight="bold">
                      {member.name}
                    </Typography>
                    <Typography variant="body2" color="primary" gutterBottom>
                      {member.role}
                    </Typography>
                    <Button 
                      startIcon={<FaLinkedin />}
                      size="small"
                      color="primary"
                      href={member.linkedIn}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ mt: 1 }}
                    >
                      Connect on LinkedIn
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Join us section */}
      <Box 
        sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          py: 6
        }}
      >
        <Container maxWidth="lg">
          <Grid 
            container 
            spacing={4} 
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid item xs={12} md={7}>
              <Typography variant="h3" component="h2" fontWeight="bold" gutterBottom>
                Ready to join our mission?
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                Become part of the Meira community today.
              </Typography>
            </Grid>
            <Grid 
              item 
              xs={12} 
              md={5}
              sx={{ 
                display: 'flex', 
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: { xs: 'flex-start', md: 'flex-end' }
              }}
            >
              <Button 
                variant="contained" 
                color="secondary" 
                size="large"
                onClick={() => navigate(APP_ROUTES.REGISTER)}
              >
                Get Started
              </Button>
              <Button 
                variant="outlined" 
                color="inherit" 
                size="large"
                onClick={() => navigate(APP_ROUTES.CONTACT)}
                sx={{ 
                  borderColor: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Contact Us
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default About;
